/**
 * Content Key Registry
 *
 * Central registry of all content keys used across the site.
 * This file documents every EditableText / editable component's
 * contentKey for debugging, content management, and auditing.
 *
 * Keys follow the pattern: {page}:{section}:{element}
 * All content is stored in Supabase KV with a "co:" prefix
 * managed by the ContentEditingContext.
 *
 * TEMPLATE TYPES:
 * - "branding" = corporate / branding site pages
 * - "store"    = e-commerce / catalog site pages
 * - "shared"   = used in both template types (header, footer, etc.)
 */

export interface ContentKeyEntry {
  /** The content key used in EditableText or similar */
  key: string;
  /** Human-readable description */
  label: string;
  /** Which page/component uses this key */
  page: string;
  /** Which template type this key belongs to */
  templateType: "branding" | "store" | "shared";
  /** Optional: the HTML tag used (h1, h2, p, span, etc.) */
  tag?: string;
}

/* ═══════════════════════════════════════
   SHARED (Header, Footer, Layout)
   ═══════════════════════════════════════ */

const sharedKeys: ContentKeyEntry[] = [
  // Header
  { key: "header:logo:text", label: "\u05D8\u05E7\u05E1\u05D8 \u05DC\u05D5\u05D2\u05D5 \u05D1\u05D4\u05D3\u05E8", page: "Header", templateType: "shared", tag: "span" },
  { key: "header:logo:sub", label: "\u05EA\u05EA \u05D8\u05E7\u05E1\u05D8 \u05DC\u05D5\u05D2\u05D5", page: "Header", templateType: "shared", tag: "span" },
  // Footer
  { key: "footer:description", label: "\u05EA\u05D9\u05D0\u05D5\u05E8 \u05D1\u05E4\u05D5\u05D8\u05E8", page: "Footer", templateType: "shared", tag: "p" },
  { key: "footer:copyright", label: "\u05E9\u05D5\u05E8\u05EA \u05D6\u05DB\u05D5\u05D9\u05D5\u05EA \u05D9\u05D5\u05E6\u05E8\u05D9\u05DD", page: "Footer", templateType: "shared", tag: "span" },
];

/* ═══════════════════════════════════════
   BRANDING TEMPLATE PAGES
   ═══════════════════════════════════════ */

const brandingKeys: ContentKeyEntry[] = [
  // Home Page
  { key: "home:hero:h1", label: "\u05DB\u05D5\u05EA\u05E8\u05EA \u05E8\u05D0\u05E9\u05D9\u05EA \u2014 \u05D3\u05E3 \u05D4\u05D1\u05D9\u05EA", page: "HomePage", templateType: "branding", tag: "h1" },
  { key: "home:hero:subtitle", label: "\u05EA\u05EA \u05DB\u05D5\u05EA\u05E8\u05EA \u2014 \u05D3\u05E3 \u05D4\u05D1\u05D9\u05EA", page: "HomePage", templateType: "branding", tag: "p" },
  { key: "home:hero:cta", label: "\u05DB\u05E4\u05EA\u05D5\u05E8 CTA \u05E8\u05D0\u05E9\u05D9", page: "HomePage", templateType: "branding", tag: "span" },
  { key: "home:hero:cta2", label: "\u05DB\u05E4\u05EA\u05D5\u05E8 CTA \u05DE\u05E9\u05E0\u05D9", page: "HomePage", templateType: "branding", tag: "span" },
  { key: "home:about:h2", label: "\u05DB\u05D5\u05EA\u05E8\u05EA \u05D0\u05D5\u05D3\u05D5\u05EA \u2014 \u05D3\u05E3 \u05D4\u05D1\u05D9\u05EA", page: "HomePage", templateType: "branding", tag: "h2" },
  { key: "home:about:text", label: "\u05D8\u05E7\u05E1\u05D8 \u05D0\u05D5\u05D3\u05D5\u05EA \u2014 \u05D3\u05E3 \u05D4\u05D1\u05D9\u05EA", page: "HomePage", templateType: "branding", tag: "p" },
  { key: "home:services:h2", label: "\u05DB\u05D5\u05EA\u05E8\u05EA \u05E9\u05D9\u05E8\u05D5\u05EA\u05D9\u05DD \u2014 \u05D3\u05E3 \u05D4\u05D1\u05D9\u05EA", page: "HomePage", templateType: "branding", tag: "h2" },
  { key: "home:cta:h2", label: "\u05DB\u05D5\u05EA\u05E8\u05EA CTA \u2014 \u05D3\u05E3 \u05D4\u05D1\u05D9\u05EA", page: "HomePage", templateType: "branding", tag: "h2" },
  { key: "home:cta:text", label: "\u05D8\u05E7\u05E1\u05D8 CTA \u2014 \u05D3\u05E3 \u05D4\u05D1\u05D9\u05EA", page: "HomePage", templateType: "branding", tag: "p" },

  // About Page
  { key: "about:hero:h1", label: "\u05DB\u05D5\u05EA\u05E8\u05EA \u05E8\u05D0\u05E9\u05D9\u05EA \u2014 \u05D0\u05D5\u05D3\u05D5\u05EA", page: "AboutPage", templateType: "branding", tag: "h1" },
  { key: "about:hero:subtitle", label: "\u05EA\u05EA \u05DB\u05D5\u05EA\u05E8\u05EA \u2014 \u05D0\u05D5\u05D3\u05D5\u05EA", page: "AboutPage", templateType: "branding", tag: "p" },
  { key: "about:story:h2", label: "\u05DB\u05D5\u05EA\u05E8\u05EA \u05D4\u05E1\u05D9\u05E4\u05D5\u05E8 \u2014 \u05D0\u05D5\u05D3\u05D5\u05EA", page: "AboutPage", templateType: "branding", tag: "h2" },
  { key: "about:story:text", label: "\u05D8\u05E7\u05E1\u05D8 \u05D4\u05E1\u05D9\u05E4\u05D5\u05E8 \u2014 \u05D0\u05D5\u05D3\u05D5\u05EA", page: "AboutPage", templateType: "branding", tag: "p" },

  // Services Page
  { key: "services:hero:h1", label: "\u05DB\u05D5\u05EA\u05E8\u05EA \u05E8\u05D0\u05E9\u05D9\u05EA \u2014 \u05E9\u05D9\u05E8\u05D5\u05EA\u05D9\u05DD", page: "ServicesPage", templateType: "branding", tag: "h1" },
  { key: "services:hero:subtitle", label: "\u05EA\u05EA \u05DB\u05D5\u05EA\u05E8\u05EA \u2014 \u05E9\u05D9\u05E8\u05D5\u05EA\u05D9\u05DD", page: "ServicesPage", templateType: "branding", tag: "p" },

  // Service A + B
  { key: "service-a:hero:h1", label: "\u05DB\u05D5\u05EA\u05E8\u05EA \u2014 \u05E9\u05D9\u05E8\u05D5\u05EA \u05D0\u05F3", page: "ServiceAPage", templateType: "branding", tag: "h1" },
  { key: "service-a:hero:subtitle", label: "\u05EA\u05EA \u05DB\u05D5\u05EA\u05E8\u05EA \u2014 \u05E9\u05D9\u05E8\u05D5\u05EA \u05D0\u05F3", page: "ServiceAPage", templateType: "branding", tag: "p" },
  { key: "service-b:hero:h1", label: "\u05DB\u05D5\u05EA\u05E8\u05EA \u2014 \u05E9\u05D9\u05E8\u05D5\u05EA \u05D1\u05F3", page: "ServiceBPage", templateType: "branding", tag: "h1" },
  { key: "service-b:hero:subtitle", label: "\u05EA\u05EA \u05DB\u05D5\u05EA\u05E8\u05EA \u2014 \u05E9\u05D9\u05E8\u05D5\u05EA \u05D1\u05F3", page: "ServiceBPage", templateType: "branding", tag: "p" },

  // Products Page
  { key: "products:hero:h1", label: "\u05DB\u05D5\u05EA\u05E8\u05EA \u2014 \u05DE\u05D5\u05E6\u05E8\u05D9\u05DD", page: "ProductsPage", templateType: "branding", tag: "h1" },
  { key: "products:hero:subtitle", label: "\u05EA\u05EA \u05DB\u05D5\u05EA\u05E8\u05EA \u2014 \u05DE\u05D5\u05E6\u05E8\u05D9\u05DD", page: "ProductsPage", templateType: "branding", tag: "p" },

  // Product A + B
  { key: "product-a:hero:h1", label: "\u05DB\u05D5\u05EA\u05E8\u05EA \u2014 \u05DE\u05D5\u05E6\u05E8 \u05D0\u05F3", page: "ProductAPage", templateType: "branding", tag: "h1" },
  { key: "product-b:hero:h1", label: "\u05DB\u05D5\u05EA\u05E8\u05EA \u2014 \u05DE\u05D5\u05E6\u05E8 \u05D1\u05F3", page: "ProductBPage", templateType: "branding", tag: "h1" },

  // Projects Page
  { key: "projects:hero:h1", label: "\u05DB\u05D5\u05EA\u05E8\u05EA \u2014 \u05EA\u05D9\u05E7 \u05E2\u05D1\u05D5\u05D3\u05D5\u05EA", page: "ProjectsPage", templateType: "branding", tag: "h1" },
  { key: "projects:hero:subtitle", label: "\u05EA\u05EA \u05DB\u05D5\u05EA\u05E8\u05EA \u2014 \u05EA\u05D9\u05E7 \u05E2\u05D1\u05D5\u05D3\u05D5\u05EA", page: "ProjectsPage", templateType: "branding", tag: "p" },

  // Consulting Page
  { key: "consulting:hero:h1", label: "\u05DB\u05D5\u05EA\u05E8\u05EA \u2014 \u05D9\u05D9\u05E2\u05D5\u05E5", page: "ConsultingPage", templateType: "branding", tag: "h1" },
  { key: "consulting:hero:subtitle", label: "\u05EA\u05EA \u05DB\u05D5\u05EA\u05E8\u05EA \u2014 \u05D9\u05D9\u05E2\u05D5\u05E5", page: "ConsultingPage", templateType: "branding", tag: "p" },

  // Contact Page
  { key: "contact:hero:title", label: "\u05DB\u05D5\u05EA\u05E8\u05EA \u05D4\u05D9\u05E8\u05D5 \u2014 \u05E6\u05D5\u05E8 \u05E7\u05E9\u05E8", page: "ContactPage", templateType: "branding", tag: "h1" },
  { key: "contact:hero:subtitle", label: "\u05EA\u05EA \u05DB\u05D5\u05EA\u05E8\u05EA \u05D4\u05D9\u05E8\u05D5 \u2014 \u05E6\u05D5\u05E8 \u05E7\u05E9\u05E8", page: "ContactPage", templateType: "branding", tag: "p" },
  { key: "contact:form:title", label: "\u05DB\u05D5\u05EA\u05E8\u05EA \u05D8\u05D5\u05E4\u05E1 \u2014 \u05E6\u05D5\u05E8 \u05E7\u05E9\u05E8", page: "ContactPage", templateType: "branding", tag: "h2" },
  { key: "contact:cta:title", label: "\u05DB\u05D5\u05EA\u05E8\u05EA CTA \u2014 \u05E6\u05D5\u05E8 \u05E7\u05E9\u05E8", page: "ContactPage", templateType: "branding", tag: "h3" },
  { key: "contact:cta:text", label: "\u05D8\u05E7\u05E1\u05D8 CTA \u2014 \u05E6\u05D5\u05E8 \u05E7\u05E9\u05E8", page: "ContactPage", templateType: "branding", tag: "p" },

  // Blog Page
  { key: "blog:page:title", label: "\u05DB\u05D5\u05EA\u05E8\u05EA \u05E2\u05DE\u05D5\u05D3 \u05D1\u05DC\u05D5\u05D2", page: "BlogPage", templateType: "branding", tag: "h1" },
  { key: "blog:dynamic-posts", label: "\u05E8\u05E9\u05D9\u05DE\u05EA \u05DE\u05D0\u05DE\u05E8\u05D9\u05DD \u05D3\u05D9\u05E0\u05DE\u05D9\u05EA (JSON)", page: "BlogPage", templateType: "branding" },
  { key: "blog-post:lead-bar:title", label: "\u05DB\u05D5\u05EA\u05E8\u05EA \u05DC\u05D9\u05D3 \u05D1\u05E8 \u2014 \u05DE\u05D0\u05DE\u05E8", page: "BlogPostPage", templateType: "branding", tag: "h3" },
  { key: "blog-sidebar:cta:title", label: "\u05DB\u05D5\u05EA\u05E8\u05EA CTA \u05E1\u05D9\u05D9\u05D3\u05D1\u05E8", page: "BlogPostPage", templateType: "branding", tag: "h3" },
  { key: "blog-sidebar:cta:subtitle", label: "\u05EA\u05EA CTA \u05E1\u05D9\u05D9\u05D3\u05D1\u05E8", page: "BlogPostPage", templateType: "branding", tag: "p" },

  // Blog Sidebar
  { key: "blog-sidebar:advantage:0", label: "\u05D9\u05EA\u05E8\u05D5\u05DF 1 \u2014 \u05E1\u05D9\u05D9\u05D3\u05D1\u05E8 \u05D1\u05DC\u05D5\u05D2", page: "BlogPostPage", templateType: "branding", tag: "span" },
  { key: "blog-sidebar:advantage:1", label: "\u05D9\u05EA\u05E8\u05D5\u05DF 2 \u2014 \u05E1\u05D9\u05D9\u05D3\u05D1\u05E8 \u05D1\u05DC\u05D5\u05D2", page: "BlogPostPage", templateType: "branding", tag: "span" },
];

/* ═══════════════════════════════════════
   STORE TEMPLATE PAGES
   ═══════════════════════════════════════ */

const storeKeys: ContentKeyEntry[] = [
  // Store Home Page
  { key: "store-home:hero:h1", label: "\u05DB\u05D5\u05EA\u05E8\u05EA \u05E8\u05D0\u05E9\u05D9\u05EA \u2014 \u05D3\u05E3 \u05D4\u05D1\u05D9\u05EA \u05D7\u05E0\u05D5\u05EA", page: "StoreHomePage", templateType: "store", tag: "h1" },
  { key: "store-home:hero:subtitle", label: "\u05EA\u05EA \u05DB\u05D5\u05EA\u05E8\u05EA \u2014 \u05D3\u05E3 \u05D4\u05D1\u05D9\u05EA \u05D7\u05E0\u05D5\u05EA", page: "StoreHomePage", templateType: "store", tag: "p" },
  { key: "store-home:hero:subtitle2", label: "\u05EA\u05EA \u05DB\u05D5\u05EA\u05E8\u05EA 2 \u2014 \u05D3\u05E3 \u05D4\u05D1\u05D9\u05EA \u05D7\u05E0\u05D5\u05EA", page: "StoreHomePage", templateType: "store", tag: "p" },
  { key: "store-home:hero:cta", label: "\u05DB\u05E4\u05EA\u05D5\u05E8 CTA \u2014 \u05D3\u05E3 \u05D4\u05D1\u05D9\u05EA \u05D7\u05E0\u05D5\u05EA", page: "StoreHomePage", templateType: "store", tag: "span" },
  { key: "store-home:categories:h2", label: "\u05DB\u05D5\u05EA\u05E8\u05EA \u05E7\u05D8\u05D2\u05D5\u05E8\u05D9\u05D5\u05EA \u2014 \u05D3\u05E3 \u05D4\u05D1\u05D9\u05EA \u05D7\u05E0\u05D5\u05EA", page: "StoreHomePage", templateType: "store", tag: "h2" },
  { key: "store-home:about:h2", label: "\u05DB\u05D5\u05EA\u05E8\u05EA \u05D0\u05D5\u05D3\u05D5\u05EA \u2014 \u05D3\u05E3 \u05D4\u05D1\u05D9\u05EA \u05D7\u05E0\u05D5\u05EA", page: "StoreHomePage", templateType: "store", tag: "h2" },
  { key: "store-home:about:text", label: "\u05D8\u05E7\u05E1\u05D8 \u05D0\u05D5\u05D3\u05D5\u05EA \u2014 \u05D3\u05E3 \u05D4\u05D1\u05D9\u05EA \u05D7\u05E0\u05D5\u05EA", page: "StoreHomePage", templateType: "store", tag: "p" },
  { key: "store-home:advantages:h2", label: "\u05DB\u05D5\u05EA\u05E8\u05EA \u05D9\u05EA\u05E8\u05D5\u05E0\u05D5\u05EA \u2014 \u05D3\u05E3 \u05D4\u05D1\u05D9\u05EA \u05D7\u05E0\u05D5\u05EA", page: "StoreHomePage", templateType: "store", tag: "h2" },
  { key: "store-home:featured:h2", label: "\u05DB\u05D5\u05EA\u05E8\u05EA \u05DE\u05D5\u05E6\u05E8\u05D9\u05DD \u05DE\u05D5\u05D1\u05D9\u05DC\u05D9\u05DD \u2014 \u05D3\u05E3 \u05D4\u05D1\u05D9\u05EA \u05D7\u05E0\u05D5\u05EA", page: "StoreHomePage", templateType: "store", tag: "h2" },
  { key: "store-home:lead:h2", label: "\u05DB\u05D5\u05EA\u05E8\u05EA \u05DC\u05D9\u05D3 \u2014 \u05D3\u05E3 \u05D4\u05D1\u05D9\u05EA \u05D7\u05E0\u05D5\u05EA", page: "StoreHomePage", templateType: "store", tag: "h2" },

  // Catalog Page
  { key: "catalog:hero:h1", label: "\u05DB\u05D5\u05EA\u05E8\u05EA \u2014 \u05E7\u05D8\u05DC\u05D5\u05D2", page: "CatalogPage", templateType: "store", tag: "h1" },
  { key: "catalog:hero:subtitle", label: "\u05EA\u05EA \u05DB\u05D5\u05EA\u05E8\u05EA \u2014 \u05E7\u05D8\u05DC\u05D5\u05D2", page: "CatalogPage", templateType: "store", tag: "p" },

  // Category Page
  { key: "category:hero:h1", label: "\u05DB\u05D5\u05EA\u05E8\u05EA \u2014 \u05E2\u05DE\u05D5\u05D3 \u05E7\u05D8\u05D2\u05D5\u05E8\u05D9\u05D4", page: "CategoryPage", templateType: "store", tag: "h1" },

  // Product Page
  { key: "product:name", label: "\u05E9\u05DD \u05DE\u05D5\u05E6\u05E8", page: "ProductPage", templateType: "store", tag: "h1" },
  { key: "product:description", label: "\u05EA\u05D9\u05D0\u05D5\u05E8 \u05DE\u05D5\u05E6\u05E8", page: "ProductPage", templateType: "store", tag: "p" },
  { key: "product:price", label: "\u05DE\u05D7\u05D9\u05E8 \u05DE\u05D5\u05E6\u05E8", page: "ProductPage", templateType: "store", tag: "span" },

  // Store About Page
  { key: "store-about:hero:h1", label: "\u05DB\u05D5\u05EA\u05E8\u05EA \u2014 \u05D0\u05D5\u05D3\u05D5\u05EA \u05D7\u05E0\u05D5\u05EA", page: "StoreAboutPage", templateType: "store", tag: "h1" },
  { key: "store-about:hero:subtitle", label: "\u05EA\u05EA \u05DB\u05D5\u05EA\u05E8\u05EA \u2014 \u05D0\u05D5\u05D3\u05D5\u05EA \u05D7\u05E0\u05D5\u05EA", page: "StoreAboutPage", templateType: "store", tag: "p" },
  { key: "store-about:story:h2", label: "\u05DB\u05D5\u05EA\u05E8\u05EA \u05D4\u05E1\u05D9\u05E4\u05D5\u05E8 \u2014 \u05D0\u05D5\u05D3\u05D5\u05EA \u05D7\u05E0\u05D5\u05EA", page: "StoreAboutPage", templateType: "store", tag: "h2" },
  { key: "store-about:story:text", label: "\u05D8\u05E7\u05E1\u05D8 \u05D4\u05E1\u05D9\u05E4\u05D5\u05E8 \u2014 \u05D0\u05D5\u05D3\u05D5\u05EA \u05D7\u05E0\u05D5\u05EA", page: "StoreAboutPage", templateType: "store", tag: "p" },
];

/* ═══════════════════════════════════════
   SEO KEYS (auto-generated pattern)
   ═══════════════════════════════════════ */

// SEO keys follow the pattern: seo:{pageId}:title | seo:{pageId}:description | seo:{pageId}:slug
// These are managed by the SeoManagerPage and stored with prefix "co:"

/* ═══════════════════════════════════════
   PAGE BUILDER KEYS (dynamic)
   ═══════════════════════════════════════ */

// Page Builder blocks are stored as JSON arrays under:
//   blocks:{pageKey}:{zoneId}
// e.g. blocks:home:bottom, blocks:about:top
// These are dynamic and not individually registered.

/* ═══════════════════════════════════════
   COMBINED REGISTRY & EXPORTS
   ═══════════════════════════════════════ */

/** All registered content keys */
export const contentKeyRegistry: ContentKeyEntry[] = [
  ...sharedKeys,
  ...brandingKeys,
  ...storeKeys,
];

/** Get keys for a specific template type */
export function getKeysForType(
  type: "branding" | "store"
): ContentKeyEntry[] {
  return contentKeyRegistry.filter(
    (k) => k.templateType === type || k.templateType === "shared"
  );
}

/** Get keys for a specific page */
export function getKeysForPage(page: string): ContentKeyEntry[] {
  return contentKeyRegistry.filter((k) => k.page === page);
}

/** Lookup a specific key's metadata */
export function getKeyMeta(key: string): ContentKeyEntry | undefined {
  return contentKeyRegistry.find((k) => k.key === key);
}

/** All unique pages that have registered keys */
export const registeredPages = [
  ...new Set(contentKeyRegistry.map((k) => k.page)),
];

/** Summary stats */
export const registryStats = {
  totalKeys: contentKeyRegistry.length,
  sharedKeys: sharedKeys.length,
  brandingKeys: brandingKeys.length,
  storeKeys: storeKeys.length,
};